import { Children, createContext, ForwardedRef, ReactNode, useContext, useEffect, isValidElement } from 'react'
import {
  FieldPath,
  FieldValues,
  get,
  RegisterOptions,
  set,
  useController,
  useFormContext,
  useFormState,
  ValidationRule
} from 'react-hook-form'
import { ErrorBoundary } from 'react-error-boundary'
import { AlertDescription } from '@chakra-ui/icons'
import {
  AlertTitle,
  ExtendedFormControlProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelOptional,
  FormLabelProps,
  Tooltip,
  useFormControlContext,
  useSafeLayoutEffect,
  VisuallyHidden,
  AlertBanner
} from '~/design_system'
import { useMsg, Msg } from '~/common/localizationUtils'
import { useIsMounting } from '~/common/nextJsUtils'
import { mergeRefs } from '~/common/reactUtils'
import { useHasChanged } from '~/hooks/usePrevious'
import { IconProps, Question } from '~/icons'

type FormControlRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<RegisterOptions<TFieldValues, TFieldName>, 'disabled'> & {
  requiredNotNull?: boolean
  valueAsBoolean?: boolean
}

interface HookFormControlContext<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TFieldName
  id?: string
  rules?: FormControlRules<TFieldValues, TFieldName>
  defaultValue?: any
  shouldUnregister?: boolean
}

export const HookFormControlContext = createContext<HookFormControlContext>({
  name: '',
  id: undefined,
  rules: undefined,
  defaultValue: undefined,
  shouldUnregister: true
})

export type HookFormControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<ExtendedFormControlProps, 'label' | 'defaultValue' | 'isRequired'> & {
  id?: string
  name: TFieldName
  rules?: FormControlRules<TFieldValues, TFieldName>
  defaultValue?: any
  shouldUnregister?: boolean
}

export const HookFormControl = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  id,
  defaultValue,
  shouldUnregister,
  rules,
  isReadOnly,
  isInvalid,
  isDisabled,
  children,
  ...chakraProps
}: HookFormControlProps<TFieldValues, TFieldName>) => {
  // Docs on shouldUnregister say you shouldn't set it to true if you are on an array field
  // so default to true if we don't see array naming convention
  const smartShouldUnregister = shouldUnregister ?? !name?.match(/\.[0-9]+\./)

  const transformedRules = useTransformRules(rules)
  const hasError = !!useFieldErrorMessage(name)

  return (
    <HookFormControlContext.Provider
      // @ts-ignore
      value={{ name, id: id ?? name, rules: transformedRules, defaultValue, shouldUnregister: smartShouldUnregister }}>
      <FormControl
        {...chakraProps}
        id={id ?? name}
        isInvalid={hasError || isInvalid}
        isRequired={Boolean(rules?.required) || Boolean(rules?.requiredNotNull)}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <>
              {Children.toArray(children).filter(c => isValidElement(c) && c.type === HookFormLabel)}
              <AlertBanner status="error">
                <AlertTitle>Application Error Occurred</AlertTitle>
                <AlertDescription>
                  {process.env.NODE_ENV === 'production' ? (
                    <Msg id="common.ui.content_failed_to_load" />
                  ) : (
                    error.message
                  )}
                </AlertDescription>
              </AlertBanner>
            </>
          )}>
          {children}
        </ErrorBoundary>
      </FormControl>
    </HookFormControlContext.Provider>
  )
}

/**
 * Default RHF required rule doesn't understand boolean false
 * @param required
 * @param defaultMessage
 */
function processRequiredRule(required?: string | ValidationRule<boolean> | boolean, defaultMessage?: string) {
  const isNully = (v: any) => v == null || v === ''

  if (required === undefined) {
    return undefined
  }
  if (typeof required === 'string' || typeof required === 'boolean') {
    return {
      // eslint-disable-next-line no-nested-ternary
      validate: (x: any) => (!isNully(x) ? true : required === true ? defaultMessage : required)
    }
  }
  return { required }
}

/**
 * Helper to fill in common messaging or behavior for rules/validation
 */
function useTransformRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  required,
  requiredNotNull,
  min,
  max,
  maxLength,
  ...otherRules
}: FormControlRules<TFieldValues, TFieldName> = {}): FormControlRules<TFieldValues, TFieldName> {
  const msg = useMsg()
  const defaultRequiredError = msg('common.ui.field_is_required')
  const _required = required === true ? defaultRequiredError : required
  const _requiredNotNull = processRequiredRule(requiredNotNull, defaultRequiredError)
  const _min =
    // eslint-disable-next-line no-nested-ternary
    typeof min === 'number'
      ? min === 0
        ? { value: min, message: msg('common.ui.positive_number') }
        : { value: min, message: msg('common.ui.field_min', { value: min }) }
      : min
  const _max = typeof max === 'number' ? { value: max, message: msg('common.ui.field_max', { value: max }) } : max
  const _maxLength =
    typeof maxLength === 'number'
      ? { value: maxLength, message: msg('common.ui.field_max_length', { max_chars: maxLength }) }
      : maxLength

  return {
    ...otherRules,
    ...(_required && { required: _required }),
    ..._requiredNotNull,
    ...(_min !== undefined && { min: _min }),
    ...(_max !== undefined && { max: _max }),
    ...(_maxLength !== undefined && { maxLength: _maxLength })
  }
}

export function makeChangeEvent({ name, value }: { name: string; value: string | number }) {
  return { type: 'change', target: { name, value } }
}

export function HookFormLabel({
  optionalIndicator,
  ...props
}: FormLabelProps & { optionalIndicator?: boolean; children: ReactNode }) {
  const LabelComponent = optionalIndicator ? FormLabelOptional : FormLabel
  return <LabelComponent display="inline-block" {...props} />
}

export function HookFormLabelTooltip({ tooltipText, ...props }: { tooltipText?: string } & IconProps) {
  const { helpTextId, setHasHelpText } = useFormControlContext()

  useSafeLayoutEffect(() => {
    if (tooltipText) setHasHelpText(true)
    else setHasHelpText(false)
  }, [tooltipText])

  if (!tooltipText) {
    return null
  }

  return (
    <>
      <Tooltip label={tooltipText} placement="top-start">
        <Question w={6} mb="4px" ml="-8px" color="blue.500" cursor="help" {...props} />
      </Tooltip>
      <VisuallyHidden id={helpTextId}>{tooltipText}</VisuallyHidden>
    </>
  )
}

export function HookFormError() {
  const { name } = useContext(HookFormControlContext)
  const errorMessage = useFieldErrorMessage(name)
  return <FormErrorMessage data-testid="form-error-message">{errorMessage}</FormErrorMessage>
}

function useFieldErrorMessage<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(name: TFieldName) {
  const state = useFormState({ name })
  return (get(state.errors, name) ?? {}).message
}

export function getRuleValue(rule?: { value: any } | any) {
  return rule?.value ?? rule
}

export function useUncontrolledField({ defaultRules }: { defaultRules?: FormControlRules } = {}) {
  const { control, register } = useFormContext()
  const {
    id,
    name,
    rules = {},
    defaultValue: defaultFromFormControl,
    shouldUnregister
  } = useContext(HookFormControlContext)
  const { defaultValues } = useFormState()
  const defaultFromForm = get(defaultValues ?? {}, name)
  const defaultValue = defaultFromForm ?? defaultFromFormControl
  const mergedRules = {
    ...defaultRules,
    ...rules,
    shouldUnregister
  }

  // Remove disabled on the field, we don't need it, Chakra handles that for us
  const { disabled, ...field } = register(
    name,
    // @ts-ignore
    mergedRules
  )

  // This pattern helps set the defaultValue into whatever state hook-form uses to track dirty states
  useEffect(() => {
    if (defaultFromForm != null || defaultValue === undefined) return
    const newValue = getFieldValueAs(defaultValue, rules)
    set(control._defaultValues, name, newValue)
  }, [])

  // Make a better onChange that can accept an Event or a raw value. If a non-event raw value
  // is received then we convert it to an event. This makes the onChange behave the same as
  // useController's onChange. Now both can just be called with the value directly, e.g. onChange(newValue)
  const onChange = (valueOrEvent: Event | any) => {
    // eslint-disable-next-line no-prototype-builtins
    const isEventLike = valueOrEvent instanceof Event || valueOrEvent?.hasOwnProperty('target')
    const payload = isEventLike ? valueOrEvent : makeChangeEvent({ name, value: valueOrEvent })
    return field.onChange(payload)
  }

  return {
    field: {
      ...field,
      id: id ?? name,
      onChange,
      defaultValue
    },
    rules: mergedRules,
    defaultValue
  }
}

type DefaultValueFormatter = <Out>(objs: UseControlledFieldReturn) => Out
type UseControlledFieldReturn = ReturnType<typeof useControlledField>

export function useControlledField<TRef extends HTMLElement>({
  defaultRules,
  defaultValueFormatter,
  ref
}: {
  defaultRules?: FormControlRules
  defaultValueFormatter?: DefaultValueFormatter
  ref?: ForwardedRef<TRef>
} = {}) {
  const {
    id,
    name,
    rules = {},
    defaultValue: defaultFromFormControl,
    shouldUnregister
  } = useContext(HookFormControlContext)
  const { defaultValues } = useFormState()
  const defaultFromForm = get(defaultValues ?? {}, name)
  const defaultValue = defaultFromForm ?? defaultFromFormControl
  const mergedRules = {
    ...defaultRules,
    ...rules
  }
  const { field, fieldState, formState } = useController({
    name,
    rules: mergedRules,
    defaultValue,
    shouldUnregister
  })

  // We'll be returning this, but we need it in a variable, so we can give it to the hook below
  const returnObj = {
    field: {
      ...field,
      id: id ?? name,
      ref: mergeRefs(field.ref, ref)
    },
    rules: mergedRules,
    fieldState,
    formState,
    defaultValue
  }

  const formattedDefaultValue = useDefaultValueFormatAndReset(
    defaultFromForm,
    defaultValue,
    defaultValueFormatter,
    rules,
    returnObj
  )

  return { ...returnObj, defaultValue: formattedDefaultValue }
}

function useDefaultValueFormatAndReset(
  defaultFromForm: any,
  defaultValue: any,
  defaultValueFormatter: DefaultValueFormatter | undefined,
  rules: ValueAsConfig,
  useControlledFieldReturn: UseControlledFieldReturn
) {
  const { control, setValue } = useFormContext()
  const { name } = useContext(HookFormControlContext)
  const isMounting = useIsMounting()

  useEffect(() => {
    if (
      isMounting &&
      // If the default value came from the call to useForm, e.g. useForm({ defaultValues: ... })
      // Then we don't need to do this resetting, this is only needed when the default value
      // is coming from the HookFormControl component, <HookFormControl defaultValue={...} />
      defaultFromForm == null &&
      defaultValue !== undefined &&
      // If a defaultValueFormatter is provided, then don't handle resetting the field for the dev,
      // they'll handle it themselves.
      !defaultValueFormatter
    ) {
      const newValue = getFieldValueAs(defaultValue, rules)
      set(control._defaultValues, name, newValue)
      setValue(name, newValue)
    }
  })

  let formattedDefaultValue = defaultValue
  const hasDefaultValueChanged = useHasChanged(defaultValue)
  if (hasDefaultValueChanged) {
    // If there is a defaultValueFormatter, then get the value from it, otherwise just use the defaultValue as-is.
    if (defaultValueFormatter) {
      formattedDefaultValue = defaultValueFormatter?.(useControlledFieldReturn)
    }
  }

  // Now whenever the formattedDefaultValue changes, let's reset the field's defaultValue so it is set in the form's
  // top-level defaultValues as well... keeps stuff in sync.
  useEffect(() => {
    if (defaultValueFormatter && defaultValue !== formattedDefaultValue) {
      const newValue = getFieldValueAs(formattedDefaultValue, rules)
      set(control._defaultValues, name, newValue)
      setValue(name, newValue)
    }
  }, [formattedDefaultValue, defaultValueFormatter])

  return formattedDefaultValue
}

type ValueAsConfig = { valueAsNumber?: boolean; valueAsDate?: boolean; setValueAs?: (value: any) => any }
const getFieldValueAs = (value: any, { valueAsNumber, valueAsDate, setValueAs }: ValueAsConfig) =>
  // eslint-disable-next-line no-nested-ternary
  value === undefined
    ? value
    : // eslint-disable-next-line no-nested-ternary
    valueAsNumber
    ? // eslint-disable-next-line no-nested-ternary
      value === ''
      ? NaN
      : value
      ? +value
      : value
    : // eslint-disable-next-line no-nested-ternary
    valueAsDate && typeof value === 'string'
    ? new Date(value)
    : setValueAs
    ? setValueAs(value)
    : value
